import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Sparkles, FileText, BarChart3, ClipboardList, Download, ShieldCheck, LogOut } from "lucide-react";
import { supabase } from "../lib/supabase";

export default function Landing({ session }) {
  const navigate = useNavigate();

  const handleLogout = async () => {
    await supabase.auth.signOut();
  };

  return (
    <div className="min-h-screen bg-canvas text-ink" data-testid="landing-page">
      {/* Top Navigation */}
      <header className="frost border-b hairline sticky top-0 z-30">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-8 py-3.5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="brand-logo">
            <div className="w-8 h-8 rounded-xl bg-ink text-white flex items-center justify-center font-semibold text-sm">
              R
            </div>
            <div className="font-semibold tracking-tighter2 text-[17px] leading-none text-ink">
              Recruit<span className="text-accent">.</span>IQ
            </div>
          </Link>
          
          <nav className="hidden md:flex items-center gap-7 text-sm text-sub">
            <a href="#how" className="hover:text-ink transition">How it works</a>
            <a href="#features" className="hover:text-ink transition">Features</a>
            <a href="#trial" className="hover:text-ink transition">Pricing</a>
          </nav>

          <div className="flex items-center gap-3">
            {session ? (
              <>
                <Link 
                  to="/app" 
                  data-testid="open-app-btn" 
                  className="btn-primary text-sm"
                >
                  Open Dashboard
                </Link>
                <button
                  onClick={handleLogout}
                  className="p-1.5 border hairline rounded-full hover:bg-haze text-ink transition-colors flex items-center justify-center"
                  title="Log Out"
                >
                  <LogOut className="h-3.5 w-3.5" />
                </button>
              </>
            ) : (
              <>
                <Link 
                  to="/login" 
                  className="text-sm text-sub hover:text-ink transition font-medium"
                >
                  Sign In
                </Link>
                <Link 
                  to="/register" 
                  data-testid="open-app-btn" 
                  className="btn-primary text-sm"
                >
                  Get Started
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      {/* HERO SECTION */}
      <section className="relative overflow-hidden">
        <div className="hero-glow absolute inset-0 -z-10" />
        <div className="max-w-[1100px] mx-auto px-6 lg:px-8 pt-24 lg:pt-32 pb-16 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-accentSoft text-accent text-sm font-medium mb-8">
            <Sparkles size={14} /> 2 Free Hiring Runs on Registration — No Credit Card Needed
          </div>
          
          <h1 className="display-1 text-5xl sm:text-6xl lg:text-7xl text-ink">
            Hire smarter.<br />
            <span className="text-sub">Decide faster.</span>
          </h1>
          
          <p className="mt-7 max-w-2xl mx-auto text-lg lg:text-xl text-sub leading-relaxed">
            Drop in a job description and a stack of resumes. Recruit.IQ ranks every candidate, builds structured interview kits, and generates recruiter-grade exports — in minutes, not days.
          </p>
          
          <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            {session ? (
              <Link 
                to="/app/new" 
                data-testid="hero-start-btn" 
                className="btn-accent inline-flex items-center gap-2 text-base px-7"
              >
                Create new project <ArrowRight size={16} />
              </Link>
            ) : (
              <Link 
                to="/register" 
                data-testid="hero-start-btn" 
                className="btn-accent inline-flex items-center gap-2 text-base px-7"
              >
                Start free trial <ArrowRight size={16} />
              </Link>
            )}
            <Link 
              to="/app" 
              data-testid="hero-projects-btn" 
              className="btn-ghost inline-flex items-center gap-2 text-base"
            >
              See dashboard
            </Link>
          </div>
          <div className="mt-7 text-xs text-soft">No signup required for preview. Bring your own AI keys in settings.</div>
        </div>

        {/* Product mock preview */}
        <div className="max-w-[1180px] mx-auto px-6 lg:px-8 pb-20">
          <div className="card overflow-hidden shadow-lift">
            <div className="bg-haze p-3 border-b hairline flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-line"></div>
              <div className="w-3 h-3 rounded-full bg-line"></div>
              <div className="w-3 h-3 rounded-full bg-line"></div>
              <div className="ml-3 text-xs text-soft font-mono">recruit.iq · senior_backend_engineer_match</div>
            </div>
            
            <div className="grid grid-cols-12 gap-0">
              <div className="col-span-12 lg:col-span-3 p-6 border-r hairline bg-haze2">
                <div className="eyebrow mb-3">Hiring confidence</div>
                <div className="text-5xl font-semibold tracking-tightest text-ink">87<span className="text-2xl text-sub">%</span></div>
                <div className="mt-4 space-y-2.5">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-sub">Strong fits</span>
                    <span className="font-mono text-ink">04</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-sub">Moderate fits</span>
                    <span className="font-mono text-ink">06</span>
                  </div>
                  <div className="flex items-center justify-between text-sm text-danger font-medium">
                    <span>At risk</span>
                    <span className="font-mono">02</span>
                  </div>
                </div>
              </div>
              
              <div className="col-span-12 lg:col-span-9 p-6">
                <div className="eyebrow mb-4">Leaderboard preview</div>
                <div className="space-y-0">
                  {[
                    ["01", "Aanya Verma", "Strong Fit", 94, "Python · FastAPI · AWS · Kafka"],
                    ["02", "Marcus Chen", "Strong Fit", 91, "Python · Postgres · K8s · Go"],
                    ["03", "Priya Kumar", "Hidden Gem", 88, "Python · Rust · Distributed systems"],
                    ["04", "Daniel Reyes", "Moderate Fit", 76, "Python · FastAPI · SQL"],
                  ].map(([rank, name, cls, score, skills]) => (
                    <div key={rank} className="grid grid-cols-12 items-center gap-4 py-3.5 border-t hairline first:border-0">
                      <div className="col-span-1 font-mono text-sub text-sm">{rank}</div>
                      <div className="col-span-4 font-medium">{name}</div>
                      <div className="col-span-2">
                        <span className={`badge ${
                          cls === "Strong Fit" ? "badge-strong" :
                          cls === "Hidden Gem" ? "badge-gem" : "badge-mod"
                        }`}>
                          {cls}
                        </span>
                      </div>
                      <div className="col-span-1 font-mono text-2xl text-ink">{score}</div>
                      <div className="col-span-4 text-sm text-sub truncate">{skills}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="bg-haze2 border-y hairline">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-8 py-24">
          <div className="text-center mb-14">
            <div className="eyebrow mb-3">How it works</div>
            <h2 className="display-2 text-4xl lg:text-5xl">
              Three steps to a defensible shortlist.
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { n: "01", icon: FileText, title: "Drop the JD & resumes", body: "Paste or upload PDF/DOCX. We auto-extract the role, skills and seniority from the JD." },
              { n: "02", icon: Sparkles, title: "AI ranks everyone", body: "Each candidate gets a match score, classification, strengths, risks, and a verdict — fully explained." },
              { n: "03", icon: Download, title: "Export & interview", body: "Generate interview kits with sharp, validation-focused questions. Export PDF and CSV reports." },
            ].map((s) => (
              <div key={s.n} className="card p-7">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-xl bg-accentSoft flex items-center justify-center">
                    <s.icon size={18} className="text-accent" />
                  </div>
                  <span className="font-mono text-sm text-soft">{s.n}</span>
                </div>
                <h3 className="text-xl font-semibold mb-2">{s.title}</h3>
                <p className="text-sub leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="max-w-[1100px] mx-auto px-6 lg:px-8 py-24">
        <div className="text-center mb-14">
          <div className="eyebrow mb-3">Built for hiring operators</div>
          <h2 className="display-2 text-4xl lg:text-5xl">
            Everything you need.<br />Nothing you don't.
          </h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {[
            { icon: BarChart3, title: "Explainable scoring", body: "Every score is broken down by required skills, experience, seniority, communication and stability. Never a black box." },
            { icon: ClipboardList, title: "Interview kits, on demand", body: "6–7 sharp questions across technical, behavioral, leadership and culture fit — with what-to-look-for cues." },
            { icon: Download, title: "Recruiter-grade exports", body: "PDF reports, candidate briefs and CSV rankings — formatted for board reviews and client hand-offs." },
            { icon: ShieldCheck, title: "Privacy by design", body: "Resumes are stored in private storage buckets, with a compliance toggle to instantly delete files from servers after parsing." },
          ].map((f) => (
            <div key={f.title} className="card p-7 card-hover">
              <div className="w-10 h-10 rounded-xl bg-haze flex items-center justify-center mb-4">
                <f.icon size={18} className="text-ink" />
              </div>
              <h3 className="text-lg font-semibold mb-2">{f.title}</h3>
              <p className="text-sub leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* PRICING / TRIAL */}
      <section id="trial" className="bg-ink text-white">
        <div className="max-w-[900px] mx-auto px-6 lg:px-8 py-24 text-center">
          <div className="eyebrow text-soft mb-3">Free Trial</div>
          <h2 className="display-2 text-4xl lg:text-5xl">
            Start with 2 free hiring runs.<br />
            <span className="text-soft">No API key required.</span>
          </h2>
          <p className="mt-6 text-lg text-soft max-w-xl mx-auto">
            Create an account to run your first two matching jobs completely free. After that, either input your own LLM API keys (paying only your server hosting cost) or pay a low pay-as-you-go credit fee per resume.
          </p>
          <div className="mt-10">
            {session ? (
              <Link 
                to="/app/new" 
                data-testid="cta-start-btn" 
                className="btn-accent inline-flex items-center gap-2 text-base px-8"
              >
                Go to App <ArrowRight size={16} />
              </Link>
            ) : (
              <Link 
                to="/register" 
                data-testid="cta-start-btn" 
                className="btn-accent inline-flex items-center gap-2 text-base px-8"
              >
                Sign Up Now <ArrowRight size={16} />
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t hairline bg-canvas">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-8 py-8 flex flex-wrap items-center justify-between gap-3 text-sm text-sub">
          <div>© {new Date().getFullYear()} Recruit.IQ — Hiring intelligence for lean teams.</div>
          <div className="font-mono text-xs text-soft">v0.2 · Apple-grade</div>
        </div>
      </footer>
    </div>
  );
}
